import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPasswordDialog({ open, onOpenChange }) {
  const [hasPassword, setHasPassword] = useState(null);
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrent(''); setPw(''); setPw2('');
    axios.get(`${API}/auth/password/status`, { withCredentials: true }).then(r => setHasPassword(!!r.data.has_password)).catch(() => setHasPassword(false));
  }, [open]);

  const policy = pw.length >= 10 && /\d/.test(pw) && /[a-zA-Z]/.test(pw);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/password/set`, { current_password: current, new_password: pw }, { withCredentials: true });
      toast.success(hasPassword ? 'Password changed' : 'Password set — you can now sign in with email & password');
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="set-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4" /> {hasPassword ? 'Change password' : 'Set a password'}</DialogTitle>
          <DialogDescription>Lets you sign in with your work email and password instead of Google. At least 10 characters with letters and numbers.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {hasPassword && <Input type="password" autoComplete="current-password" placeholder="Current password" value={current} onChange={e => setCurrent(e.target.value)} required data-testid="set-password-current" />}
          <Input type="password" autoComplete="new-password" placeholder="New password" value={pw} onChange={e => setPw(e.target.value)} required data-testid="set-password-new" />
          <Input type="password" autoComplete="new-password" placeholder="Repeat new password" value={pw2} onChange={e => setPw2(e.target.value)} required data-testid="set-password-repeat" />
          {pw && !policy && <p className="text-xs text-amber-700" data-testid="set-password-policy">Needs 10+ characters including letters and numbers.</p>}
          <Button type="submit" disabled={busy || !policy || pw !== pw2 || (hasPassword && !current)} className="w-full" data-testid="set-password-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (hasPassword ? 'Change password' : 'Save password')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
